import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'Keywords',
  data () {
    return {
      isLoading: {
        keywords: false,
        crawl: false,
        relatedKeywords: false,
        process: false,
        savingKeywords: false,
        aiKeywords: false
      },

      newUploadKeywords: null,
      relatedKeywordInput: '',
      aiInput: '',

      uploadedKeywords: [],
      relatedKeywords: [],
      aiKeywords: [],

      keywordsReadyToUpload: [],
      newRelatedKeywords: [],
      newAiKeywords: [],
      selectedGSCKeywords: [],

      filterKeyword: '',

      keywordRules: [
        value => !value || value?.split(/[\r\n\t]+/)?.length < 5000 || 'You can add up to 5000 at once',
        value => !value || value?.split(/[\r\n\t]+/)?.length > 1 || 'Enter at least 2 keywords'
      ],

      tabActive: 0,
      tabs: ['Select from inactive keywords', 'Upload a list', 'Search related keywords', 'Ask AI'],

      tableOptions: {
        itemsPerPage: 100,
        page: 1,
        sortBy: ['estimated_search_volume'],
        sortDesc: [true]
      },
      footerProps: {
        'items-per-page-options': [100, 500, 1000]
      },
      tableGSC: {
        headers: [
          { text: 'Keyword', value: 'keyword' },
          { text: 'Search volume', value: 'estimated_search_volume' },
          { text: 'Impressions', value: 'google_impressions' },
          { text: 'Clicks', value: 'google_clicks' },
          { text: 'CTR', value: 'google_ctr' },
          { text: 'Rank', value: 'google_position' }
        ]
      },
      tableRelated: {
        headers: [
          { text: 'Keyword', value: 'keyword' },
          { text: 'Search volume', value: 'estimated_search_volume' },
          { text: 'Difficulty', value: 'difficulty' },
          { text: 'Impressions', value: 'google_impressions_last' },
          { text: '', value: 'article_id' }
        ]
      },
      tableAI: {
        headers: [
          { text: 'Keyword', value: 'keyword' },
          { text: 'Search volume', value: 'estimated_search_volume' },
          { text: 'Difficulty', value: 'difficulty' },
          { text: 'Impressions', value: 'google_impressions_last' },
          { text: 'Source', value: 'source' },
          { text: '', value: 'article_id' }
        ]
      }
    }
  },
  async mounted () {
    await this.loadData()
  },
  watch: {
    async $route () {
      await this.loadData()
    },
    async tableOptions () {
      if (!this.isLoading.keywords) {
        await this.fillKeywordsTable()
      }
    }
  },
  computed: {
    ...mapGetters([
      'user',
      'workspaces',
      'activeWorkspace',
      'activeContentplan',
      'keywords'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    keywordsArray () {
      return this.keywords?.keywords || []
    },
    keywordLimitAvailable () {
      if (!this.activeContentplan || !this.keywords) return 0

      return this.activeContentplan?.keyword_limit - this.keywords.keywordCounts?.inPlanCount
    },
    selectedCount () {
      if (!this.keywords?.keywords) return 0

      return (this.keywords?.keywords || []).filter(k => !k.excluded).length
    },
    keywordsHint () {
      if (!this.newUploadKeywords?.length) return 'Enter a list of keywords, one per line'
      if (/,+/u.test(this.newUploadKeywords)) return 'Enter one keyword per line. Don\'t use commas to seperate keywords'
      if (!/^[\p{L}\p{N}\s\n]+$/u.test(this.newUploadKeywords)) return 'Keywords with punctuation or special symbols will be ignored'
      return ''
    },
    countSelectedKeywords () {
      if (this.tabActive === 0) return this.selectedGSCKeywords?.length || 0
      if (this.tabActive === 1) return this.keywordsReadyToUpload?.length || 0
      if (this.tabActive === 2) return this.newRelatedKeywords?.length || 0
      if (this.tabActive === 3) return this.newAiKeywords?.length || 0

      return 0
    },
    relatedKeywordsArray () {
      // return empty array if related keywords is string
      if (!this.relatedKeywords || typeof this.relatedKeywords === 'string') return []
      return this.relatedKeywords
    },
    aiKeywordsArray () {
      // return empty array if related keywords is string
      if (!this.aiKeywords || typeof this.aiKeywords === 'string') return []
      return this.aiKeywords
    }
  },
  methods: {
    ...mapActions([
      'loadContentplanKeywords',
      'uploadKeywords',
      'updateKeywords',
      'queueKeywords',
      'startCrawl',
      'getRelatedKeywords',
      'loadCrawl',
      'processKeywords',
      'getRelatedKeywordsNlp'
    ]),
    ...mapMutations([
      'clearCrawl'
    ]),
    async loadData () {
      if (this.isLoading.keywords) return
      this.isLoading.keywords = true

      try {
        this.fillKeywordsTable()
      } catch (err) {
        console.error(err)
      }
    },
    async fillKeywordsTable () {
      this.isLoading.keywords = true

      await this.loadContentplanKeywords({
        workspaceId: this.workspaceId,
        contentplanId: this.contentplanId,
        q: this.filterKeyword || '.*',
        limit: this.tableOptions.itemsPerPage || 100,
        page: this.tableOptions.page || 1,
        url: this.filterUrl,
        status: ['not_in_plan'],
        source: ['google_search_console', 'upload', 'related'],
        sortColumn: this.tableOptions.sortBy[0] || 'estimated_search_volume',
        sortDirection: this.tableOptions.sortDesc[0] ? 'desc' : 'asc'
      })

      this.isLoading.keywords = false
    },
    clearSelections () {
      this.selectedGSCKeywords = []
      this.keywordsReadyToUpload = []
      this.newRelatedKeywords = []
    },
    countNewUploadKeywords () {
      if (!this.newUploadKeywords?.length) return 0
      // count keywords one per line
      return this.newUploadKeywords?.split(/[\r\n\t]+/)?.filter(k => k).length || 0
    },
    async handleAddKeywordsClick () {
      this.isLoading.crawl = true

      if (this.newRelatedKeywords?.length) {
        await this.saveKeywords(this.newRelatedKeywords, 'related')
        this.newRelatedKeywords = []
      }

      if (this.newAiKeywords?.length) {
        await this.saveKeywords(this.newAiKeywords, 'related')
        this.newAiKeywords = []
      }

      if (this.keywordsReadyToUpload?.length) {
        await this.saveKeywords(this.keywordsReadyToUpload, 'upload')
        this.keywordsReadyToUpload = []
      }

      if (this.selectedGSCKeywords?.length) {
        await this.addGSCKeywordsToQueue()
      }

      await this.crawl()
    },
    async processUploadedKeywords () {
      const keywords = this.newUploadKeywords.split(/[\r\n\t]+/)?.filter(k => k?.length)
      this.isLoading.process = true

      try {
        this.uploadedKeywords = await this.processKeywords({
          contentplanId: this.contentplanId,
          keywordsInput: keywords
        })
        this.newUploadKeywords = null
      } catch (e) {
        console.error(e)
      }

      this.isLoading.process = false
    },
    async loadRelatedKeywords () {
      if (!this.relatedKeywordInput) return
      this.isLoading.relatedKeywords = true

      try {
        this.relatedKeywords = await this.getRelatedKeywords({
          contentplanId: this.contentplanId,
          keywords: [this.relatedKeywordInput]
        })
      } catch (err) {
        this.errorMessage = err
      }

      this.isLoading.relatedKeywords = false
    },
    async loadRelatedKeywordsNlp () {
      if (!this.aiInput) return
      this.isLoading.aiKeywords = true

      try {
        this.aiKeywords = await this.getRelatedKeywordsNlp({
          prompt: this.aiInput,
          contentplanId: this.contentplanId
        })
      } catch (err) {
        this.errorMessage = err
      }

      this.isLoading.aiKeywords = false
    },
    async addGSCKeywordsToQueue () {
      this.isLoading.queue = true

      this.selectedGSCKeywords.sort((a, b) => {
        return b.estimated_search_volume - a.estimated_search_volume
      })

      const selectedKeywordIds = this.selectedGSCKeywords.slice(0, this.keywordLimitAvailable).map(el => el.id)

      try {
        await this.queueKeywords({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          keywordIds: selectedKeywordIds,
          queue: true
        })
        this.selectedGSCKeywords = []
      } catch (e) {
        console.error(e)
      }

      this.isLoading.queue = false
    },
    async saveKeywords (keywordsObject, source) {
      if (!keywordsObject || typeof keywordsObject !== 'object') return
      this.isLoading.savingKeywords = true

      try {
        await this.uploadKeywords({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          keywordsObject,
          source,
          queue: 1
        })
      } catch (e) {
        console.error(e)
      }

      this.isLoading.savingKeywords = false
    },
    sortGscKeywords (index, isDescending) {
      this.sortColumn = index[0]
      this.sortDirection = isDescending ? 'desc' : 'asc'
    },
    async crawl () {
      this.clearCrawl()

      await this.startCrawl({
        workspaceId: this.workspaceId,
        contentplanId: this.contentplanId,
        keyword_limit_available: this.keywordLimitAvailable
      })

      this.isLoading.crawl = false

      this.$router.push({
        name: 'Crawl',
        params: {
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId
        }
      })
    }
  }
}
